/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import moment from 'moment';
import * as constants from '../../../../common/constants';
import utils from '../../../../common/utils';

const MODULES_WITH_TYPE_DISPLAY_TYPE_OVERRIDE = new Set(constants.MODULES_WITH_TYPE_DISPLAY_TYPE_OVERRIDE);

const normalizeColumn = (column) => ({
  ...column,
  id: column?.id || column?.key,
  label: column?.label || column?.name
});

export const normalizeColumnConfig = (columnConfig = []) => {
  if (!Array.isArray(columnConfig)) {
    return [];
  }

  return columnConfig.map(normalizeColumn).filter((column) => column.id);
};

const pickEntityColumns = (gridColumnConfig, entityType) => {
  if (!gridColumnConfig || typeof gridColumnConfig !== 'object') {
    return [];
  }

  const { variants, default: defaultColumns } = gridColumnConfig;

  if (entityType && Array.isArray(variants?.[entityType])) {
    return variants[entityType];
  }

  if (entityType && Array.isArray(gridColumnConfig[entityType])) {
    return gridColumnConfig[entityType];
  }

  if (Array.isArray(defaultColumns)) {
    return defaultColumns;
  }

  if (Array.isArray(gridColumnConfig.default)) {
    return gridColumnConfig.default;
  }

  return [];
};

export const resolveGridColumnConfig = (gridColumnConfig, entityType) => {
  if (!gridColumnConfig) {
    return [];
  }

  const columns = Array.isArray(gridColumnConfig) ? gridColumnConfig : pickEntityColumns(gridColumnConfig, entityType);

  const normalized = normalizeColumnConfig(columns);

  if (!entityType) {
    return normalized;
  }

  return normalized.filter((column) => !column.entityTypes?.length || column.entityTypes.includes(entityType));
};

export const resolveExportColumnConfig = ({ columnConfig, entityType }) =>
  resolveGridColumnConfig(columnConfig, entityType);

const columnConfigUsesExportableFlag = (columns = []) => columns.some((column) => column?.isExportable != null);

const isColumnExportable = (column, columns = []) => {
  if (!column?.id) {
    return false;
  }

  if (column.isExportable === false || column.exportColumn === false) {
    return false;
  }

  if (columnConfigUsesExportableFlag(columns)) {
    return column.isExportable === true;
  }

  return true;
};

export const getExportableColumns = (columnConfig = []) =>
  columnConfig.filter((column) => isColumnExportable(column, columnConfig));

export const resolveExportableColumns = ({ columnConfig, entityType }) =>
  getExportableColumns(resolveExportColumnConfig({ columnConfig, entityType }));

export const splitExportColumns = (columns = []) => {
  const midpoint = Math.ceil(columns.length / 2);

  return {
    leftColumns: columns.slice(0, midpoint),
    rightColumns: columns.slice(midpoint)
  };
};

export const getDefaultSelectedExportColumns = (exportableColumns = []) =>
  exportableColumns.reduce((acc, column) => {
    const selectedByDefault = column.exportDefaultSelected ?? (column.isExportable === true || !column.hideColumn);

    if (selectedByDefault) {
      acc[column.id] = true;
    }

    return acc;
  }, {});

const EXPORT_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';

const hasDateValue = (value) => value != null && String(value).trim() !== '';

/** Export endpoints scope rows by the searched date range, so empty dates must stay out of the payload. */
export const getExportDateRange = (lastPayload = {}) => {
  const { startDate, endDate } = lastPayload || {};

  return {
    ...(hasDateValue(startDate) && { startDate }),
    ...(hasDateValue(endDate) && { endDate })
  };
};

const getRowTimestamp = (row) => {
  if (typeof row?.createdDate === 'number' && !Number.isNaN(row.createdDate)) {
    return row.createdDate;
  }

  const raw = row?.serverDateTime ?? row?.dateStamp;
  if (raw == null || raw === '') {
    return null;
  }

  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }

  const parsed = utils.toEpochUTC(raw);
  if (parsed != null) {
    return parsed;
  }

  const fallback = moment.utc(raw);
  return fallback.isValid() ? fallback.valueOf() : null;
};

/** Export bounds from loaded rows: min/max timestamps across all rows (sort-order invariant). */
export const getRowsDateRange = (rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return {};
  }

  let minTimestamp = null;
  let maxTimestamp = null;

  rows.forEach((row) => {
    const timestamp = getRowTimestamp(row);
    if (timestamp == null) {
      return;
    }
    if (minTimestamp == null || timestamp < minTimestamp) {
      minTimestamp = timestamp;
    }
    if (maxTimestamp == null || timestamp > maxTimestamp) {
      maxTimestamp = timestamp;
    }
  });

  if (minTimestamp == null && maxTimestamp == null) {
    return {};
  }

  const resolvedStart = minTimestamp ?? maxTimestamp;
  const resolvedEnd = maxTimestamp ?? minTimestamp;

  return {
    startDate: moment.utc(resolvedStart).format(EXPORT_DATE_FORMAT),
    endDate: moment.utc(resolvedEnd).format(EXPORT_DATE_FORMAT)
  };
};

const mapTitleSearchExportSearchProperties = (searchProperties = []) => {
  if (!Array.isArray(searchProperties)) {
    return searchProperties;
  }

  return searchProperties.map((property) =>
    property === constants.BOOK_ID_NAME ? constants.TITLE_SEARCH_ID_FIELD : property
  );
};

/** Title search export must scope by document id, not bookId, and must not send an empty searchField. */
export const buildTitleSearchExportFilters = (lastPayload = {}) => {
  const { searchField, searchProperties, ...restFilters } = lastPayload || {};
  const normalizedSearchField = String(searchField ?? '').trim();

  return {
    ...restFilters,
    ...(normalizedSearchField && { searchField: normalizedSearchField }),
    ...(searchProperties && {
      searchProperties: mapTitleSearchExportSearchProperties(searchProperties)
    })
  };
};

/** Title search has no date filter, so `/search/list/export` still scopes rows by the ids on screen. */
export const getTitleSearchIdBounds = (rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return { firstRequestId: null, lastRequestId: null };
  }

  const idField = constants.TITLE_SEARCH_BOUND_ID_FIELDS.find((field) =>
    rows.some((row) => row?.[field] != null && row[field] !== '')
  );

  if (!idField) {
    return { firstRequestId: null, lastRequestId: null };
  }

  return {
    firstRequestId: rows[0]?.[idField] ?? null,
    lastRequestId: rows[rows.length - 1]?.[idField] ?? null
  };
};

const isValidCountExportBoundId = (id) => {
  const normalized = String(id ?? '').trim();
  if (!normalized) {
    return false;
  }

  return normalized !== constants.ALL_BOOKS && normalized !== constants.ALL_COURSES;
};

const getCountRowIdentifierId = (row) => {
  const idField = constants.TRENDS_COUNT_BOUND_ID_FIELDS.find((field) => {
    const value = row?.[field];
    return value != null && String(value).trim() !== '';
  });

  if (!idField) {
    return '';
  }

  return String(row[idField]).trim();
};

/** Trends count export scopes by first/last identifierId from loaded count rows plus search dates. */
export const getTrendsCountIdentifierBounds = (rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return { firstRequestId: null, lastRequestId: null };
  }

  const ids = rows.map(getCountRowIdentifierId).filter(isValidCountExportBoundId);

  if (!ids.length) {
    return { firstRequestId: null, lastRequestId: null };
  }

  return {
    firstRequestId: ids[0],
    lastRequestId: ids[ids.length - 1]
  };
};

/** Date range for export payloads — only from loaded row timestamps, never search date pickers. */
export const resolveExportDateRange = ({ rows } = {}) => getRowsDateRange(rows);

/** Shared exportPayload body: search filters plus row-scoped date bounds. */
export const buildRowScopedExportPayload = (lastPayload = {}, rows = []) => {
  const { startDate, endDate, ...filters } = lastPayload || {};

  return {
    ...filters,
    ...resolveExportDateRange({ rows })
  };
};

export const mapExportPropertyIds = (columnIds = [], exportModule = '') =>
  columnIds.map((columnId) => {
    if (columnId === constants.URL_PARAMS.TYPE && MODULES_WITH_TYPE_DISPLAY_TYPE_OVERRIDE.has(exportModule)) {
      return constants.DISPLAY_TYPE_KEY;
    }

    // Agent column ids are namespaced client-side; the API expects the bare agent name.
    if (
      exportModule === constants.AUDIT_EXPORT_MODULE.OTEL_TRENDS_COUNT &&
      String(columnId).startsWith(constants.OTEL_AGENT_FEATURE_ID_PREFIX)
    ) {
      return String(columnId).slice(constants.OTEL_AGENT_FEATURE_ID_PREFIX.length);
    }

    return columnId;
  });
